import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBranch } from '../contexts/BranchContext';

// Real-time duration timer for occupied tables
const TableTimer = ({ startTime }) => {
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
        if (!startTime) {
            setElapsed('-');
            return;
        }

        const calculateElapsed = () => {
            const start = new Date(startTime).getTime();
            const now = new Date().getTime();
            const diffMs = Math.max(0, now - start);
            
            const diffSecs = Math.floor(diffMs / 1000);
            const hrs = Math.floor(diffSecs / 3600);
            const mins = Math.floor((diffSecs % 3600) / 60);
            const secs = diffSecs % 60;

            const pad = (num) => String(num).padStart(2, '0');
            return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
        };

        setElapsed(calculateElapsed());
        const timer = setInterval(() => {
            setElapsed(calculateElapsed());
        }, 1000);

        return () => clearInterval(timer);
    }, [startTime]);

    return <span>{elapsed}</span>;
};

const isBirthdayToday = (dob) => {
    if (!dob) return false;
    const dobDate = new Date(dob);
    const today = new Date();
    return dobDate.getMonth() === today.getMonth() && dobDate.getDate() === today.getDate();
};

const Customers = ({ companyInfo, currentUser, API_URL, getAuthHeaders }) => {
    const { branches, loadBranches } = useBranch();
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const branchDropdownRef = useRef(null);

    useEffect(() => {
        if (branches && branches.length === 1) {
            const onlyBranchId = branches[0].id;
            if (selectedBranch !== onlyBranchId) {
                setSelectedBranch(onlyBranchId);
            }
        }
    }, [branches, selectedBranch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
                setShowBranchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const headers = getAuthHeaders();
        const token = headers.Authorization ? headers.Authorization.replace('Bearer ', '') : null;
        if (token && branches.length === 0) {
            loadBranches(token);
        }
    }, [branches.length, getAuthHeaders, loadBranches]);

    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('clients'); // 'clients' or 'tables'
    
    // Manual Customer Add Modal
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [newDob, setNewDob] = useState('');
    const [addError, setAddError] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    // Order Placement States
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [assignedTable, setAssignedTable] = useState('');
    const [tables, setTables] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');
    const [menuSearchQuery, setMenuSearchQuery] = useState('');
    
    // Admin Cart for selected Customer
    const [cart, setCart] = useState([]);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Table Session & Checkout Invoice Modal States
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [redeemPointsChecked, setRedeemPointsChecked] = useState(false);
    const [applyBirthdayDiscountChecked, setApplyBirthdayDiscountChecked] = useState(false);
    const [pointsToRedeemInput, setPointsToRedeemInput] = useState(0);
    const [activeCheckoutTableId, setActiveCheckoutTableId] = useState(null);
    const [showCheckoutConfirmation, setShowCheckoutConfirmation] = useState(false);

    // Delete Customer States
    const [deleteConfirmCustomer, setDeleteConfirmCustomer] = useState(null); // customer object pending deletion
    const [isDeleting, setIsDeleting] = useState(false);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    // Load Customers
    const loadCustomers = useCallback(async () => {
        setIsLoading(true);
        try {
            const branchParam = (currentUser?.company_id === 38 && selectedBranch) ? `?branch_id=${selectedBranch}` : '';
            const res = await fetch(`${API_URL}/api/admin/company38/customers${branchParam}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });
            const json = await res.json();
            if (json.success) {
                setCustomers(json.data || []);
            } else {
                showToast(json.message || 'Failed to load customers', 'error');
            }
        } catch (err) {
            console.error('Error fetching customers:', err);
            showToast('Network error loading customers', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [API_URL, getAuthHeaders, selectedBranch, currentUser]);

    // Delete Customer Handler
    const handleDeleteCustomer = async () => {
        if (!deleteConfirmCustomer) return;
        setIsDeleting(true);
        try {
            const res = await fetch(`${API_URL}/api/admin/company38/customers/${deleteConfirmCustomer.id}`, {
                method: 'DELETE',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
            });
            const json = await res.json();
            if (json.success) {
                showToast(json.message || 'Customer deleted successfully', 'success');
                setDeleteConfirmCustomer(null);
                if (selectedCustomer?.id === deleteConfirmCustomer.id) {
                    setSelectedCustomer(null);
                    setCart([]);
                }
                loadCustomers();
            } else {
                showToast(json.message || 'Failed to delete customer', 'error');
            }
        } catch (err) {
            console.error('Error deleting customer:', err);
            showToast('Network error deleting customer', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    // Load Tables Statuses (Admin Only Company 38)
    const loadTables = useCallback(async () => {
        try {
            const branchParam = (currentUser?.company_id === 38 && selectedBranch) ? `?branch_id=${selectedBranch}` : '';
            const res = await fetch(`${API_URL}/api/admin/company38/tables-status${branchParam}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });
            const json = await res.json();
            if (json.success) {
                setTables(json.data || []);
            }
        } catch (err) {
            console.error('Error loading tables:', err);
        }
    }, [API_URL, getAuthHeaders, selectedBranch, currentUser]);

    // Load Menu & Categories
    const loadMenu = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/menu`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });
            const json = await res.json();
            if (json.success) {
                const validItems = (json.data || []).filter(item =>
                    !item.name.toLowerCase().includes('placeholder') &&
                    item.category !== 'add-new' &&
                    item.is_available !== false
                );
                setMenuItems(validItems);
                
                // Extract unique categories
                const cats = ['all', ...new Set(validItems.map(item => item.category).filter(Boolean))];
                setCategories(cats);
            }
        } catch (err) {
            console.error('Error loading menu:', err);
        }
    }, [API_URL, getAuthHeaders]);

    useEffect(() => {
        loadCustomers();
        loadTables();
        loadMenu();
    }, [loadCustomers, loadTables, loadMenu]);

    // Add Customer Handler
    const handleAddCustomer = async (e) => {
        e.preventDefault();
        setAddError('');
        setIsAdding(true);

        if (!/^\d{10}$/.test(newPhone.trim())) {
            setAddError('Phone number must be exactly 10 digits.');
            setIsAdding(false);
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/admin/company38/customers`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    username: newUsername.trim(),
                    phone: newPhone.trim(),
                    dob: newDob || null,
                    branch_id: (currentUser?.company_id === 38) ? selectedBranch : null
                })
            });
            const json = await res.json();
            if (json.success) {
                showToast('Customer added successfully!', 'success');
                setIsAddModalOpen(false);
                setNewUsername('');
                setNewPhone('');
                setNewDob('');
                loadCustomers();
            } else {
                setAddError(json.message || 'Failed to add customer');
            }
        } catch (err) {
            console.error('Error adding customer:', err);
            setAddError('Network error. Please try again.');
        } finally {
            setIsAdding(false);
        }
    };

    // View Bill / Invoice Details Handler
    const handleViewBill = async (tableId) => {
        try {
            const res = await fetch(`${API_URL}/api/admin/company38/table-session/${tableId}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });
            const json = await res.json();
            if (json.success && json.data) {
                setInvoiceData(json.data);
                setActiveCheckoutTableId(tableId);
                setRedeemPointsChecked(false);
                setApplyBirthdayDiscountChecked(false);
                setShowCheckoutConfirmation(false);
                
                // Initialize default points redemption input
                const currentPts = json.data.customer?.points || 0;
                const pointVal = parseFloat(json.data.redeemSettings?.point_value || 1);
                const maxPoints = Math.min(currentPts, Math.floor(json.data.total / pointVal));
                setPointsToRedeemInput(maxPoints);
                
                setIsInvoiceModalOpen(true);
            } else {
                showToast(json.message || 'Failed to load bill details', 'error');
            }
        } catch (err) {
            console.error('Error fetching table session:', err);
            showToast('Network error loading bill details', 'error');
        }
    };


    // Calculate Dynamic invoice totals on frontend state changes
    const calculateTotals = () => {
        if (!invoiceData) return { subtotal: 0, cgstAmount: 0, sgstAmount: 0, festDiscountAmount: 0, birthdayDiscount: 0, redeemedDiscount: 0, total: 0 };
        
        const subtotal = invoiceData.subtotal || 0;
        const cgstAmount = invoiceData.cgstAmount || 0;
        const sgstAmount = invoiceData.sgstAmount || 0;
        const festDiscountAmount = invoiceData.festDiscountAmount || 0;

        let birthdayDiscount = 0;
        if (applyBirthdayDiscountChecked && invoiceData.customer?.dob && invoiceData.redeemSettings) {
            const hasDob = isBirthdayToday(invoiceData.customer.dob);
            if (hasDob) {
                const val = parseFloat(invoiceData.redeemSettings.dob_discount_value || 0);
                if (invoiceData.redeemSettings.dob_discount_type === 'percentage') {
                    birthdayDiscount = subtotal * (val / 100);
                } else {
                    birthdayDiscount = val;
                }
            }
        }

        let redeemedDiscount = 0;
        if (redeemPointsChecked && invoiceData.customer && invoiceData.redeemSettings) {
            const pointVal = parseFloat(invoiceData.redeemSettings.point_value || 1);
            redeemedDiscount = parseInt(pointsToRedeemInput || 0) * pointVal;
        }

        const total = Math.max(0, subtotal + cgstAmount + sgstAmount - festDiscountAmount - birthdayDiscount - redeemedDiscount);
        return {
            subtotal,
            cgstAmount,
            sgstAmount,
            festDiscountAmount,
            birthdayDiscount,
            redeemedDiscount,
            total
        };
    };

    // Print Receipt logic
    const handlePrintBill = (totals) => {
        if (!invoiceData) return;
        const customer = invoiceData.customer;
        const itemsList = invoiceData.items.map(item => `
            <tr>
                <td style="padding: 6px 0; font-size: 13px;">${item.item_name} x ${item.quantity}</td>
                <td style="padding: 6px 0; font-size: 13px; text-align: right;">₹${item.total.toFixed(2)}</td>
            </tr>
        `).join('');

        // Get QR codes from company settings (stored as array of URL strings)
        let qrCodes = companyInfo?.qr_codes || [];
        if (typeof qrCodes === 'string') {
            try { qrCodes = JSON.parse(qrCodes); } catch { qrCodes = []; }
        }
        if (!Array.isArray(qrCodes)) qrCodes = [];

        // Resolve relative URLs to absolute
        const resolvedQrUrls = qrCodes
            .filter(q => typeof q === 'string' && q.trim())
            .map(q => q.startsWith('/') ? `${API_URL}${q}` : q);

        const upiId = companyInfo?.upi_id;

        // Build the payment section: show QR images + UPI ID together
        const hasQr = resolvedQrUrls.length > 0;
        const hasUpi = upiId && upiId.trim();

        const qrSection = (hasQr || hasUpi) ? `
            <div class="divider"></div>
            <div class="qr-section">
                <p style="margin: 0 0 10px 0; font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px;">Pay Here</p>
                ${hasQr ? resolvedQrUrls.map(url => `
                    <div style="margin-bottom: 8px;">
                        <img src="${url}" alt="QR Code" class="qr-img" />
                    </div>
                `).join('') : ''}
                ${hasUpi ? `
                    <p style="margin: 8px 0 0 0; font-size: 12px; font-weight: bold;">UPI ID</p>
                    <p style="margin: 3px 0 0 0; font-size: 13px; font-weight: bold; color: #1a1a1a;">${upiId}</p>
                ` : ''}
            </div>
        ` : '';

        const printWindow = window.open('', '_blank', 'width=600,height=700');
        if (!printWindow) {
            showToast('Popup blocker prevented printing. Please allow popups.', 'error');
            return;
        }

        printWindow.document.write(`
            <html>
                <head>
                    <title>Bill - Table ${invoiceData.orders[0]?.table_number || ''}</title>
                    <style>
                        body {
                            font-family: 'Courier New', Courier, monospace;
                            padding: 20px;
                            color: #000;
                            max-width: 300px;
                            margin: 0 auto;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 20px;
                        }
                        .header h2 {
                            margin: 0 0 5px 0;
                            font-size: 20px;
                        }
                        .header p {
                            margin: 0;
                            font-size: 12px;
                        }
                        .divider {
                            border-top: 1px dashed #000;
                            margin: 10px 0;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                        }
                        .totals td {
                            padding: 4px 0;
                            font-size: 13px;
                        }
                        .totals .grand-total {
                            font-size: 16px;
                            font-weight: bold;
                        }
                        .qr-section {
                            text-align: center;
                            margin: 12px 0;
                        }
                        .qr-img {
                            width: 160px;
                            height: 160px;
                            object-fit: contain;
                            display: block;
                            margin: 0 auto;
                            border: 1px solid #ddd;
                            padding: 4px;
                        }
                        .footer {
                            text-align: center;
                            margin-top: 25px;
                            font-size: 11px;
                        }
                        @media print {
                            .qr-img { width: 140px; height: 140px; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>${companyInfo?.name || 'Restaurant'}</h2>
                        <p>Table: ${(invoiceData.orders[0]?.table_number === 0 || invoiceData.orders[0]?.table_number === '0') ? (parseInt(companyInfo?.id) === 37 ? 'Delivery' : 'No Table') : invoiceData.orders[0]?.table_number}</p>
                        <p>Date: ${new Date().toLocaleString()}</p>
                        ${customer ? `<p>Client: ${customer.username}</p>` : ''}
                        ${parseInt(companyInfo?.id) === 38 && invoiceData.orders ? (() => {
                            const orderNums = [...new Set(invoiceData.orders.map(o => o.order_number).filter(Boolean))].sort((a, b) => a - b);
                            if (orderNums.length > 0) {
                                return `<p style="font-weight: bold; font-size: 14px; margin-top: 5px;">Order Number: ${orderNums.join(', ')}</p>`;
                            }
                            return '';
                        })() : ''}
                    </div>
                    <div class="divider"></div>
                    <table>
                        <tbody>
                            ${itemsList}
                        </tbody>
                    </table>
                    <div class="divider"></div>
                    <table class="totals">
                        <tbody>
                            <tr>
                                <td>Subtotal:</td>
                                <td style="text-align: right;">₹${totals.subtotal.toFixed(2)}</td>
                            </tr>
                            ${invoiceData.cgst > 0 ? `<tr>
                                <td>CGST (${invoiceData.cgst}%):</td>
                                <td style="text-align: right;">+₹${totals.cgstAmount.toFixed(2)}</td>
                            </tr>` : ''}
                            ${invoiceData.sgst > 0 ? `<tr>
                                <td>SGST (${invoiceData.sgst}%):</td>
                                <td style="text-align: right;">+₹${totals.sgstAmount.toFixed(2)}</td>
                            </tr>` : ''}
                            ${totals.festDiscountAmount > 0 ? `
                            <tr>
                                <td>${invoiceData.festivalName ? invoiceData.festivalName + ' Offer' : 'Festival Discount'}:</td>
                                <td style="text-align: right;">-₹${totals.festDiscountAmount.toFixed(2)}</td>
                            </tr>` : ''}
                            ${totals.birthdayDiscount > 0 ? `
                            <tr>
                                <td>Birthday Disc:</td>
                                <td style="text-align: right;">-₹${totals.birthdayDiscount.toFixed(2)}</td>
                            </tr>` : ''}
                            ${totals.redeemedDiscount > 0 ? `
                            <tr>
                                <td>Points Disc:</td>
                                <td style="text-align: right;">-₹${totals.redeemedDiscount.toFixed(2)}</td>
                            </tr>` : ''}
                            <tr class="grand-total">
                                <td style="padding-top: 10px;">TOTAL TO PAY:</td>
                                <td style="text-align: right; padding-top: 10px;">₹${totals.total.toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>
                    ${qrSection}
                    <div class="divider"></div>
                    <div class="footer">
                        <p>Thank you for dining with us!</p>
                        <p>Powered by DineFlow</p>
                    </div>
                    <script>
                        window.onload = function() {
                            window.print();
                            window.close();
                        };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };


    // Checkout table API request handler
    const handleCheckoutTable = async (totals, andPrint = false) => {
        if (!activeCheckoutTableId) return;
        setIsCheckingOut(true);
        try {
            if (andPrint) {
                handlePrintBill(totals);
            }

            const bodyPayload = {
                tableId: activeCheckoutTableId,
                customerId: invoiceData.customer?.id || null,
                redeemedPoints: redeemPointsChecked ? parseInt(pointsToRedeemInput || 0) : 0,
                applyBirthdayDiscount: applyBirthdayDiscountChecked
            };

            const res = await fetch(`${API_URL}/api/admin/company38/checkout-table`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(bodyPayload)
            });

            const json = await res.json();
            if (json.success) {
                showToast('Table checked out and freed successfully!', 'success');
                setIsInvoiceModalOpen(false);
                setInvoiceData(null);
                setActiveCheckoutTableId(null);
                loadTables();
                loadCustomers();
            } else {
                showToast(json.message || 'Checkout failed', 'error');
            }
        } catch (err) {
            console.error('Error during table checkout:', err);
            showToast('Network error during checkout', 'error');
        } finally {
            setIsCheckingOut(false);
        }
    };

    // Filter customers
    const filteredCustomers = customers.filter(c =>
        c.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phone.includes(searchQuery)
    );

    // Cart Handlers
    const addToCart = (item) => {
        setCart(prev => {
            const existing = prev.find(i => i.id === item.id);
            if (existing) {
                return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
            }
            return [...prev, { ...item, quantity: 1 }];
        });
        showToast(`Added ${item.name} to order list`, 'success');
    };

    const updateCartQty = (id, change) => {
        setCart(prev => prev.map(item => {
            if (item.id === id) {
                const newQty = item.quantity + change;
                return newQty > 0 ? { ...item, quantity: newQty } : null;
            }
            return item;
        }).filter(Boolean));
    };

    const getCartTotal = () => {
        return cart.reduce((sum, item) => sum + (item.price_inr * item.quantity), 0);
    };

    // Place Order Handler
    const handlePlaceOrder = async () => {
        if (!selectedCustomer) {
            showToast('Please select a customer first.', 'error');
            return;
        }
        if (assignedTable === '' || assignedTable === null || assignedTable === undefined) {
            showToast('Please assign a table.', 'error');
            return;
        }
        if (cart.length === 0) {
            showToast('Please add at least one item to order.', 'error');
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/orders`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    table_number: parseInt(assignedTable),
                    items: cart.map(i => ({
                        id: i.id,
                        name: i.name,
                        quantity: i.quantity,
                        price_inr: i.price_inr,
                        price_usd: i.price_usd || 0,
                        discount_percentage: i.discount_percentage || 0
                    })),
                    currency: companyInfo?.currency || 'INR',
                    payment_method: 'cash',
                    customer_id: selectedCustomer.id,
                    customer_name: selectedCustomer.username
                })
            });
            const json = await res.json();
            if (json.success) {
                showToast('Order placed successfully!', 'success');
                setCart([]);
                setSelectedCustomer(null);
                setAssignedTable('');
                loadCustomers(); // refresh totals
                loadTables();    // refresh tables
            } else {
                showToast(json.message || 'Failed to place order', 'error');
            }
        } catch (err) {
            console.error('Error placing order:', err);
            showToast('Network error placing order', 'error');
        }
    };

    // Filter Menu Items
    const filteredMenuItems = menuItems.filter(item => {
        const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
        const matchesSearch = item.name.toLowerCase().includes(menuSearchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const activeTotals = calculateTotals();
    const customer = invoiceData?.customer;
    const isBday = customer?.dob && isBirthdayToday(customer.dob);

    return (
        <div className="digital-emerald-theme min-h-screen bg-gray-50 p-4 sm:p-6 transition-all duration-500 fade-in text-gray-800">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Top Stats Banner */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-emerald-600 to-teal-700 p-6 rounded-2xl shadow-xl text-white">
                    <div>
                        <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
                            <i className="fas fa-users-cog"></i> Admin Customer Hub
                        </h1>
                        <p className="text-emerald-100/80 text-sm mt-1">Manage loyalty clients, assign tables, and place orders directly on their behalf.</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {currentUser?.company_id === 38 && (
                            <div className="relative" ref={branchDropdownRef}>
                                <button
                                    onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                                    title={selectedBranch ? (branches.find(b => b.id === selectedBranch)?.name || 'Branch') : 'All Branches'}
                                    className="bg-teal-900/40 border border-teal-500/30 text-white hover:bg-teal-900/60 px-4 py-2.5 rounded-xl font-bold text-xs tracking-wider transition-all flex items-center gap-2 shadow-md active:scale-95"
                                >
                                    <i className="fas fa-store"></i>
                                    <span>
                                        {selectedBranch ? (() => {
                                            const fullBName = branches.find(b => b.id === selectedBranch)?.name || 'Branch';
                                            if (currentUser?.company_id === 38) {
                                                const cleanBName = fullBName.replace(/^Brew\s+Theory\s*-\s*/i, '');
                                                return cleanBName.length > 15 ? cleanBName.slice(0, 15) + '...' : cleanBName;
                                            }
                                            return fullBName;
                                        })() : 'All Branches'}
                                    </span>
                                    <i className={`fas fa-chevron-down text-[10px] transition-transform duration-300 ${showBranchDropdown ? 'rotate-180' : ''}`}></i>
                                </button>
                                {showBranchDropdown && (
                                    <div className="absolute right-0 mt-2 bg-white text-gray-800 rounded-xl shadow-xl border border-gray-100 py-1.5 z-50 w-48 max-h-64 overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
                                        {branches.length > 1 && (
                                        <button
                                            onClick={() => {
                                                setSelectedBranch(null);
                                                setShowBranchDropdown(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-xs hover:bg-emerald-50 transition-all flex items-center justify-between ${!selectedBranch ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700'}`}
                                        >
                                            <span>All Branches</span>
                                            {!selectedBranch && <i className="fas fa-check text-[10px] text-emerald-600"></i>}
                                        </button>
                                        )}
                                        {branches.map(branch => (
                                            <button
                                                key={branch.id}
                                                onClick={() => {
                                                    setSelectedBranch(branch.id);
                                                    setShowBranchDropdown(false);
                                                }}
                                                className={`w-full text-left px-4 py-2 text-xs hover:bg-emerald-50 transition-all flex items-center justify-between ${selectedBranch === branch.id ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700'}`}
                                            >
                                                <span className="truncate">{branch.name}</span>
                                                {selectedBranch === branch.id && <i className="fas fa-check text-[10px] text-emerald-600"></i>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            onClick={() => {
                                setViewMode(viewMode === 'clients' ? 'tables' : 'clients');
                                loadTables();
                            }}
                            className="bg-teal-900/40 border border-teal-500/30 text-white hover:bg-teal-900/60 px-4 py-2.5 rounded-xl font-bold text-xs tracking-wider transition-all flex items-center gap-2 shadow-md active:scale-95"
                        >
                            <i className={viewMode === 'clients' ? 'fas fa-table' : 'fas fa-users'}></i>
                            {viewMode === 'clients' ? 'TABLE VIEW' : 'CLIENT VIEW'}
                        </button>
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="bg-white text-teal-800 hover:bg-emerald-50 px-4 py-2.5 rounded-xl font-bold text-xs tracking-wider transition-all flex items-center gap-2 shadow-lg hover:shadow-white/20 active:scale-95"
                        >
                            <i className="fas fa-plus"></i> ADD NEW CLIENT
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left Panel - Customers Table or Table View */}
                    <div className={`${viewMode === 'tables' ? 'lg:col-span-12' : 'lg:col-span-6'} bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col min-h-[500px]`}>
                        {viewMode === 'clients' ? (
                            <>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="relative flex-1">
                                        <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                                        <input
                                            type="text"
                                            placeholder="Search client name or phone..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-xs"
                                        />
                                    </div>
                                    <button 
                                        onClick={loadCustomers} 
                                        className="p-2.5 text-gray-400 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200 rounded-xl transition-all"
                                        title="Refresh List"
                                    >
                                        <i className="fas fa-sync-alt"></i>
                                    </button>
                                </div>

                                <div className="flex-1 overflow-x-auto">
                                    {isLoading ? (
                                        <div className="text-center py-20 text-gray-400 text-xs">
                                            <i className="fas fa-circle-notch animate-spin text-2xl text-emerald-500 mb-2"></i>
                                            <p>Retrieving client profiles...</p>
                                        </div>
                                    ) : filteredCustomers.length === 0 ? (
                                        <div className="text-center py-20 text-gray-400">
                                            <i className="fas fa-users text-4xl text-gray-200 mb-2"></i>
                                            <p className="text-sm">No customers registered yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {filteredCustomers.map(customer => (
                                                <div
                                                    key={customer.id}
                                                    onClick={() => {
                                                        setSelectedCustomer(customer);
                                                        setAssignedTable('');
                                                        setCart([]);
                                                    }}
                                                    className={`p-4 rounded-xl border transition-all cursor-pointer flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ${selectedCustomer?.id === customer.id ? 'border-emerald-500 bg-emerald-50/50 shadow-md ring-1 ring-emerald-500' : 'border-gray-100 hover:border-emerald-300 hover:bg-gray-50/30'}`}
                                                >
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-gray-900 text-sm">{customer.username}</span>
                                                        </div>
                                                        <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                                            <span><i className="fas fa-phone mr-1"></i>{customer.phone}</span>
                                                            {customer.dob && (
                                                                <span><i className="fas fa-birthday-cake mr-1"></i>{new Date(customer.dob).toLocaleDateString()}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end border-t sm:border-t-0 pt-2.5 sm:pt-0 border-gray-100">
                                                        <div className="flex items-center gap-2 text-right">
                                                            <div className="text-[10px] text-gray-400">
                                                                <div>Orders: <span className="font-black text-gray-700">{customer.order_count}</span></div>
                                                                <div>Spent: <span className="font-black text-gray-700">{companyInfo?.currency || 'INR'} {parseFloat(customer.total_spent).toFixed(2)}</span></div>
                                                            </div>
                                                            <span className="bg-emerald-100 text-emerald-800 font-black text-[10px] px-2.5 py-1 rounded-full border border-emerald-200">
                                                                {customer.points} Pts
                                                            </span>
                                                        </div>
                                                        <button 
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                setDeleteConfirmCustomer(customer); 
                                                            }}
                                                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors mr-1 shrink-0"
                                                            title="Delete Client"
                                                        >
                                                            <i className="fas fa-trash-alt text-xs"></i>
                                                        </button>
                                                        <i className={`fas fa-chevron-right text-gray-300 transition-transform ${selectedCustomer?.id === customer.id ? 'translate-x-1 text-emerald-500' : ''}`}></i>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Table View Layout */}
                                <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
                                    <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                                        <i className="fas fa-table text-emerald-600"></i> Dining Table Statuses
                                    </h3>
                                    <button 
                                        onClick={loadTables} 
                                        className="p-2.5 text-gray-400 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200 rounded-xl transition-all"
                                        title="Refresh Tables"
                                    >
                                        <i className="fas fa-sync-alt text-xs"></i>
                                    </button>
                                  </div>
                                  <div className="flex-1 overflow-y-auto max-h-[600px] pr-1">
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                          {tables.map(table => {
                                              const isOccupied = table.session_id !== null && table.session_id !== undefined;
                                              const allDelivered = isOccupied && parseInt(table.total_orders) > 0 && parseInt(table.delivered_orders) >= parseInt(table.total_orders);
                                              return (
                                                  <div 
                                                      key={table.id}
                                                      className={`p-3 rounded-2xl border transition-all flex flex-col justify-between gap-2 ${
                                                          !isOccupied
                                                              ? 'border-emerald-100 bg-emerald-50/10'
                                                              : allDelivered
                                                              ? 'border-violet-200 bg-violet-50/20'
                                                              : 'border-orange-200 bg-orange-50/20'
                                                      }`}
                                                  >
                                                      <div className="space-y-2">
                                                          <div className="flex items-center gap-1.5">
                                                              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white flex-shrink-0 ${
                                                                  !isOccupied ? 'bg-emerald-600' : allDelivered ? 'bg-violet-500' : 'bg-orange-500'
                                                              }`}>
                                                                  {table.table_number}
                                                              </span>
                                                              <div className="min-w-0">
                                                                  <span className="font-black text-gray-900 text-xs block truncate">Table {table.table_number}</span>
                                                                  {(table.group_name || table.table_name) && (
                                                                      <span className="text-[9px] text-gray-400 block truncate">
                                                                          {[table.group_name, table.table_name].filter(Boolean).join(' - ')}
                                                                      </span>
                                                                  )}
                                                              </div>
                                                          </div>

                                                          {isOccupied ? (
                                                              <div className={`text-[10px] text-gray-600 space-y-0.5 bg-white p-2 rounded-xl border shadow-sm ${allDelivered ? 'border-violet-100' : 'border-orange-100'}`}>
                                                                  <div className="font-bold text-gray-900 flex items-center gap-1 truncate">
                                                                      <i className={`fas fa-user text-[9px] ${allDelivered ? 'text-violet-400' : 'text-orange-400'}`}></i>
                                                                      <span className="truncate">{table.customer_name || 'Guest'}</span>
                                                                  </div>
                                                                  {table.customer_phone && (
                                                                      <div className="text-[9px] text-gray-500 truncate">
                                                                          <i className="fas fa-phone mr-0.5"></i>{table.customer_phone}
                                                                      </div>
                                                                  )}
                                                                  <div className="text-[9px] text-gray-500 flex items-center gap-0.5">
                                                                      <i className={`fas fa-clock ${allDelivered ? 'text-violet-400' : 'text-orange-400'}`}></i>
                                                                      <TableTimer startTime={table.session_start_at} />
                                                                  </div>
                                                                  {/* All Delivered Status */}
                                                                  {allDelivered && (
                                                                      <div className="mt-1 pt-1 border-t border-violet-100 flex items-center gap-1 text-[9px] font-black text-violet-700">
                                                                          <i className="fas fa-check-double text-violet-500"></i>
                                                                          All delivered · Awaiting bill
                                                                      </div>
                                                                  )}
                                                              </div>
                                                          ) : (
                                                              <div className="text-[10px] font-bold text-emerald-700 flex items-center gap-1">
                                                                  <i className="fas fa-check-circle text-emerald-500"></i> Available
                                                              </div>
                                                          )}
                                                      </div>

                                                      {isOccupied && (
                                                          <div className="border-t border-gray-100/50 pt-2">
                                                              <button
                                                                  onClick={() => handleViewBill(table.id)}
                                                                  className={`px-2 py-1.5 font-bold rounded-xl text-[10px] flex items-center gap-1 transition-all border w-full justify-center ${
                                                                      allDelivered
                                                                          ? 'bg-violet-50 hover:bg-violet-100 text-violet-700 border-violet-200'
                                                                          : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-100'
                                                                  }`}
                                                                  title="View Bill Details"
                                                              >
                                                                  <i className="fas fa-file-invoice-dollar text-[9px]"></i> BILL
                                                              </button>
                                                          </div>
                                                      )}
                                                  </div>
                                              );
                                          })}
                                      </div>
                                      {tables.length === 0 && (
                                          <div className="text-center py-20 text-gray-400">
                                              <i className="fas fa-table text-4xl text-gray-200 mb-2"></i>
                                              <p className="text-sm">No dining tables configured</p>
                                          </div>
                                      )}
                                  </div>
                            </>
                        )}
                    </div>

                    {/* Right Panel - Ordering/Assign Flow */}
                    {viewMode === 'clients' && (
                        <div className="lg:col-span-6 flex flex-col gap-6">
                        {/* Table Assignment Panel */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                            <h2 className="text-sm font-black tracking-wider uppercase text-gray-400 mb-4">
                                1. Target Client & Table
                            </h2>
                            {selectedCustomer ? (
                                <div className="space-y-4">
                                    <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center justify-between">
                                        <div>
                                            <div className="text-[10px] uppercase font-black text-emerald-700">Ordering For</div>
                                            <div className="font-bold text-gray-900">{selectedCustomer.username} ({selectedCustomer.phone})</div>
                                        </div>
                                        <button 
                                            onClick={() => { setSelectedCustomer(null); setAssignedTable(''); setCart([]); }} 
                                            className="text-[10px] font-bold text-red-500 hover:text-red-700"
                                        >
                                            CLEAR
                                        </button>
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-gray-400 mb-1.5">Assign Dining Table</label>
                                        <div className="grid grid-cols-4 gap-2.5">
                                            {/* Show "No Table" option for all companies except 37 */}
                                            {parseInt(companyInfo?.id) !== 37 && (
                                                <button
                                                    onClick={() => setAssignedTable(0)}
                                                    className={`py-2 px-3 text-xs font-bold rounded-xl border transition-all ${
                                                        String(assignedTable) === '0'
                                                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                                                            : 'bg-gray-50 border-gray-200 hover:border-emerald-300 text-gray-700'
                                                    }`}
                                                >
                                                    No Table
                                                </button>
                                            )}
                                            {tables.map(table => {
                                                const isOccupied = table.session_id !== null && table.session_id !== undefined;
                                                return (
                                                    <button
                                                        key={table.id}
                                                        disabled={isOccupied}
                                                        onClick={() => setAssignedTable(table.table_number)}
                                                        className={`py-2 px-3 text-xs font-bold rounded-xl border transition-all ${
                                                            isOccupied
                                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                                : String(assignedTable) === String(table.table_number)
                                                                ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                                                                : 'bg-gray-50 border-gray-200 hover:border-emerald-300 text-gray-700'
                                                        }`}
                                                    >
                                                        Table {table.table_number} {isOccupied && '(Occupied)'}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {tables.length === 0 && (
                                            <p className="text-[11px] text-red-500">No active tables found. Add them in the Tables tab first.</p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-10 text-gray-400 text-xs">
                                    <i className="fas fa-user-circle text-4xl text-gray-200 mb-2"></i>
                                    <p>Select a client from the left list to begin table assignment & ordering.</p>
                                </div>
                            )}
                        </div>

                        {/* Menu and Ordering Section */}
                        {selectedCustomer && (assignedTable !== '' && assignedTable !== null) && (
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                                <div className="flex justify-between items-center border-b border-gray-50 pb-3">
                                    <h2 className="text-sm font-black tracking-wider uppercase text-gray-400">
                                        2. Choose Menu Items
                                    </h2>
                                    <div className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-black">
                                        {String(assignedTable) === '0' ? (parseInt(companyInfo?.id) === 37 ? 'Delivery' : 'No Table') : `Table ${assignedTable}`}
                                    </div>
                                </div>

                                {/* Category Filter & Search */}
                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        placeholder="Search menu items..."
                                        value={menuSearchQuery}
                                        onChange={(e) => setMenuSearchQuery(e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-xs"
                                    />
                                    <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                                        {categories.map(cat => (
                                            <button
                                                key={cat}
                                                onClick={() => setActiveCategory(cat)}
                                                className={`px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap transition-all uppercase ${activeCategory === cat ? 'bg-slate-900 text-white shadow-sm' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                                            >
                                                {cat}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Menu Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
                                    {filteredMenuItems.map(item => (
                                        <div key={item.id} className="p-3 bg-gray-50/50 border border-gray-100 rounded-xl flex justify-between items-center">
                                            <div className="min-w-0 pr-2">
                                                <p className="font-bold text-gray-900 text-xs truncate">{item.name}</p>
                                                <p className="text-[10px] text-gray-500 mt-0.5">{companyInfo?.currency || 'INR'} {item.price_inr}</p>
                                            </div>
                                            <button
                                                onClick={() => addToCart(item)}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded-lg transition-all active:scale-95 flex-shrink-0"
                                                title="Add item"
                                            >
                                                <i className="fas fa-plus text-[10px]"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Cart / Order Overview */}
                                {cart.length > 0 && (
                                    <div className="border-t border-gray-100 pt-4 space-y-3">
                                        <h3 className="text-xs font-black uppercase text-gray-400">Order Summary</h3>
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {cart.map(item => (
                                                <div key={item.id} className="flex justify-between items-center text-xs">
                                                    <span className="font-bold text-gray-800">{item.name}</span>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white">
                                                            <button onClick={() => updateCartQty(item.id, -1)} className="px-2 py-0.5 bg-gray-50 hover:bg-gray-100 transition-all font-black text-[10px]">-</button>
                                                            <span className="px-2.5 font-bold text-gray-700 text-[10px]">{item.quantity}</span>
                                                            <button onClick={() => updateCartQty(item.id, 1)} className="px-2 py-0.5 bg-gray-50 hover:bg-gray-100 transition-all font-black text-[10px]">+</button>
                                                        </div>
                                                        <span className="font-black text-gray-900 w-16 text-right">{companyInfo?.currency || 'INR'} {(item.price_inr * item.quantity).toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="flex justify-between items-center border-t border-gray-100 pt-3 text-sm">
                                            <span className="font-black text-gray-900">Total:</span>
                                            <span className="font-black text-emerald-700">{companyInfo?.currency || 'INR'} {getCartTotal().toFixed(2)}</span>
                                        </div>

                                        <button
                                            onClick={handlePlaceOrder}
                                            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-3 rounded-xl shadow-lg transition-all active:scale-98 text-xs tracking-wider"
                                        >
                                            PLACE ORDER FOR {selectedCustomer.username.toUpperCase()}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    )}
                </div>

                {/* Delete Customer Confirmation Modal */}
                {deleteConfirmCustomer && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-gray-100 animate-scale-up">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-black text-red-600 flex items-center gap-2">
                                    <i className="fas fa-trash-alt"></i> Delete Client?
                                </h3>
                                <button 
                                    onClick={() => setDeleteConfirmCustomer(null)}
                                    className="text-gray-400 hover:text-gray-600 transition-all"
                                >
                                    <i className="fas fa-times text-lg"></i>
                                </button>
                            </div>
                            <div className="space-y-4">
                                <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-2xl text-xs space-y-2">
                                    <div className="font-bold flex items-center gap-1.5 text-red-700">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <span>Warning: Permanent Data Loss</span>
                                    </div>
                                    <p className="text-gray-700 leading-relaxed">
                                        Are you sure you want to delete client <strong>{deleteConfirmCustomer.username}</strong>?
                                    </p>
                                    <p className="text-gray-700 leading-relaxed font-semibold">
                                        This will permanently delete the client and all associated orders, loyalty points, and history. This action cannot be undone.
                                    </p>
                                </div>
                                <div className="flex gap-2.5 pt-2">
                                    <button
                                        onClick={handleDeleteCustomer}
                                        disabled={isDeleting}
                                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all shadow-md"
                                    >
                                        <i className="fas fa-trash-alt"></i> {isDeleting ? 'Deleting...' : 'Delete Client'}
                                    </button>
                                    <button
                                        onClick={() => setDeleteConfirmCustomer(null)}
                                        className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add Customer Overlay Modal */}
                {isAddModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-gray-100 animate-scale-up">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                                    <i className="fas fa-user-plus text-emerald-600"></i> Add New Client
                                </h3>
                                <button 
                                    onClick={() => { setIsAddModalOpen(false); setAddError(''); }}
                                    className="text-gray-400 hover:text-gray-600 transition-all"
                                >
                                    <i className="fas fa-times text-lg"></i>
                                </button>
                            </div>

                            {addError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs font-semibold mb-4 flex items-center gap-2">
                                    <i className="fas fa-exclamation-circle text-sm"></i>
                                    <span>{addError}</span>
                                </div>
                            )}

                            <form onSubmit={handleAddCustomer} className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-1">Username</label>
                                    <input
                                        type="text"
                                        required
                                        value={newUsername}
                                        onChange={(e) => setNewUsername(e.target.value)}
                                        placeholder="e.g. John Doe"
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        required
                                        value={newPhone}
                                        onChange={(e) => setNewPhone(e.target.value)}
                                        placeholder="10-digit number"
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-xs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase text-gray-400 mb-1">Date of Birth</label>
                                    <input
                                        type="date"
                                        value={newDob}
                                        onChange={(e) => setNewDob(e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all text-xs text-gray-700"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isAdding}
                                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-3 rounded-xl transition-all shadow-md active:scale-95 text-xs tracking-wider"
                                >
                                    {isAdding ? 'ADDING CLIENT...' : 'ADD CLIENT'}
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Table Checkout & Invoice Detail Modal */}
                {isInvoiceModalOpen && invoiceData && (
                    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm overflow-y-auto">
                        <div className="min-h-full flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 my-8">
                            <div className="flex justify-between items-center mb-4 border-b pb-3">
                                <div>
                                    <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                                        <i className="fas fa-file-invoice text-emerald-600"></i> {(invoiceData.orders[0]?.table_number === 0 || invoiceData.orders[0]?.table_number === '0') ? (parseInt(companyInfo?.id) === 37 ? 'Delivery' : 'No Table') : `Table ${invoiceData.orders[0]?.table_number}`} Active Bill
                                    </h3>
                                    {parseInt(companyInfo?.id) === 38 && invoiceData.orders && (() => {
                                        const orderNums = [...new Set(invoiceData.orders.map(o => o.order_number).filter(Boolean))].sort((a, b) => a - b);
                                        if (orderNums.length > 0) {
                                            return <p className="text-gray-900 font-bold text-xs mt-1">Order Number: {orderNums.join(', ')}</p>;
                                        }
                                        return null;
                                    })()}
                                </div>
                                <button 
                                    onClick={() => { setIsInvoiceModalOpen(false); setInvoiceData(null); setShowCheckoutConfirmation(false); }}
                                    className="text-gray-400 hover:text-gray-600 transition-all"
                                >
                                    <i className="fas fa-times text-lg"></i>
                                </button>
                            </div>

                            {/* Seated customer summary */}
                            {customer ? (
                                <div className="mb-4 bg-emerald-50/50 border border-emerald-100 p-3 rounded-2xl text-xs space-y-1">
                                    <div className="font-bold text-gray-800 flex items-center gap-1">
                                        <i className="fas fa-user-circle text-emerald-600"></i> Seated Customer: {customer.username}
                                    </div>
                                    <div className="text-gray-500">Phone: {customer.phone}</div>
                                    <div className="text-emerald-700 font-bold">Loyalty Balance: {customer.points} points</div>
                                    {isBday && (
                                        <div className="text-pink-600 font-black flex items-center gap-1 mt-1">
                                            <i className="fas fa-birthday-cake animate-bounce"></i> Happy Birthday! Birthday reward available.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="mb-4 bg-gray-50 border border-gray-100 p-3 rounded-2xl text-xs text-gray-500 italic">
                                    No customer profile associated with this dining session.
                                </div>
                            )}

                            {/* Order items details */}
                            <div className="space-y-2.5 max-h-56 overflow-y-auto border-b border-gray-100 pb-3 pr-1">
                                <h4 className="text-[10px] font-black uppercase text-gray-400">Order Lines</h4>
                                {invoiceData.items.map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-xs">
                                        <div className="min-w-0 pr-2">
                                            <span className="font-bold text-gray-800 block truncate">{item.item_name}</span>
                                            <span className="text-[10px] text-gray-400">{item.quantity} x ₹{parseFloat(item.price).toFixed(2)}</span>
                                        </div>
                                        <span className="font-black text-gray-900">₹{item.total.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Invoice settings/points checkboxes */}
                            {customer && invoiceData.redeemSettings && (
                                <div className="my-3 space-y-2 bg-gray-50/50 p-3 rounded-2xl border border-gray-100">
                                    <h4 className="text-[10px] font-black uppercase text-gray-400 mb-1">Administrative Adjustments</h4>
                                    
                                    {/* Birthday discount checkbox */}
                                    {isBday && (
                                        <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-pink-700">
                                            <input
                                                type="checkbox"
                                                checked={applyBirthdayDiscountChecked}
                                                onChange={(e) => setApplyBirthdayDiscountChecked(e.target.checked)}
                                                className="w-4 h-4 rounded text-pink-600 focus:ring-pink-500 border-gray-300"
                                            />
                                            Apply Birthday Discount
                                        </label>
                                    )}

                                    {/* Points discount checkbox */}
                                    {customer.points > 0 && (
                                        <div className="space-y-1.5">
                                            <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-emerald-800">
                                                <input
                                                    type="checkbox"
                                                    checked={redeemPointsChecked}
                                                    onChange={(e) => {
                                                        setRedeemPointsChecked(e.target.checked);
                                                        if (!e.target.checked) setPointsToRedeemInput(0);
                                                    }}
                                                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300"
                                                />
                                                Redeem Points
                                            </label>
                                            
                                            {redeemPointsChecked && (
                                                <div className="flex items-center gap-2 pl-6">
                                                    <span className="text-[10px] text-gray-500">Points to redeem:</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={customer.points}
                                                        value={pointsToRedeemInput}
                                                        onChange={(e) => {
                                                            const val = Math.max(0, Math.min(customer.points, parseInt(e.target.value) || 0));
                                                            setPointsToRedeemInput(val);
                                                        }}
                                                        className="w-20 px-2 py-1 rounded-lg border border-gray-300 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-transparent outline-none font-bold"
                                                    />
                                                    <span className="text-[10px] text-emerald-600 font-bold">(Value: ₹{(pointsToRedeemInput * parseFloat(invoiceData.redeemSettings.point_value || 1)).toFixed(2)})</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Summary Invoice totals */}
                            <div className="space-y-1.5 bg-gray-50 p-4 rounded-2xl text-xs mb-5 font-semibold">
                                <div className="flex justify-between text-gray-500">
                                    <span>Subtotal</span>
                                    <span>₹{activeTotals.subtotal.toFixed(2)}</span>
                                </div>
                                {(invoiceData.cgst > 0) && (
                                    <div className="flex justify-between text-gray-500">
                                        <span>CGST ({invoiceData.cgst}%)</span>
                                        <span>+₹{activeTotals.cgstAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {(invoiceData.sgst > 0) && (
                                    <div className="flex justify-between text-gray-500">
                                        <span>SGST ({invoiceData.sgst}%)</span>
                                        <span>+₹{activeTotals.sgstAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {activeTotals.festDiscountAmount > 0 && (
                                    <div className="flex justify-between font-bold text-red-600">
                                        <span>{invoiceData.festivalName ? `${invoiceData.festivalName} Offer` : 'Festival Discount'} (-{invoiceData.festDiscountPercent}%)</span>
                                        <span>-₹{activeTotals.festDiscountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {activeTotals.birthdayDiscount > 0 && (
                                    <div className="flex justify-between font-bold text-pink-600">
                                        <span>🎂 Birthday Discount</span>
                                        <span>-₹{activeTotals.birthdayDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                                {activeTotals.redeemedDiscount > 0 && (
                                    <div className="flex justify-between font-bold text-emerald-600">
                                        <span>Points Redeemed ({pointsToRedeemInput} pts)</span>
                                        <span>-₹{activeTotals.redeemedDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-black text-base text-gray-900 border-t border-gray-200 pt-2 mt-2">
                                    <span>Total to Pay</span>
                                    <span>₹{activeTotals.total.toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            {showCheckoutConfirmation ? (
                                <div className="bg-orange-50 border border-orange-200 p-4 rounded-2xl text-xs space-y-3 mb-2 animate-fade-in w-full">
                                    <div className="font-bold text-orange-800 flex items-center gap-1.5">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <span>Confirm Table Checkout?</span>
                                    </div>
                                    <p className="text-gray-600">
                                        If the bill has been printed and you wish to complete the checkout (free the table), click <strong>PRINT BILL & CHECKOUT</strong>. Otherwise, click <strong>CANCEL</strong>.
                                    </p>
                                    <div className="flex gap-2.5 pt-1">
                                        <button
                                            onClick={() => handleCheckoutTable(activeTotals, false)}
                                            disabled={isCheckingOut}
                                            className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all shadow-md"
                                        >
                                            <i className="fas fa-cash-register"></i> {isCheckingOut ? 'CHECKING OUT...' : 'PRINT BILL & CHECKOUT'}
                                        </button>
                                        <button
                                            onClick={() => setShowCheckoutConfirmation(false)}
                                            className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all"
                                        >
                                            CANCEL
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col sm:flex-row gap-2.5 w-full">
                                    <button
                                        onClick={() => handlePrintBill(activeTotals)}
                                        className="flex-1 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all shadow-sm"
                                    >
                                        <i className="fas fa-print"></i> PRINT BILL ONLY
                                    </button>
                                    <button
                                        onClick={() => {
                                            handlePrintBill(activeTotals);
                                            setShowCheckoutConfirmation(true);
                                        }}
                                        className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all shadow-md"
                                    >
                                        <i className="fas fa-print"></i> PRINT BILL & CHECKOUT
                                    </button>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                )}

            </div>
            {/* Custom Toast Notification */}
            {toast.show && (
                <div className={`fixed top-4 right-4 z-[9999] flex items-center gap-2.5 px-5 py-3 rounded-xl shadow-2xl transition-all animate-fade-in-down ${toast.type === 'error' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'}`}>
                    <i className={`fas ${toast.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'} text-base`}></i>
                    <span className="font-semibold text-xs">{toast.message}</span>
                </div>
            )}
        </div>
    );
};

export default Customers;
